import { Sort } from '@agent/core/basket/models/basket';
import {
  buildCommonsRequests,
  buildPersonalRequest,
  Criteria,
  FieldList,
} from '@agent/core/channel-digit/visio/models/visio-request-queries';
import { BasketVisioLabels, IVisioRequestQueryResult } from '@agent/core/channel-digit/visio/models/visio.requests.interfaces';
import { OpentokService } from '@agent/core/channel-digit/visio/opentok/opentok.service';
import { HttpResponse } from '@angular/common/http';
import { ElementRef, Injectable } from '@angular/core';
import { toJson } from 'angular';
import { BehaviorSubject, catchError, combineLatest, map, Observable, Subject, take, throwError } from 'rxjs';

import { AgentConfigFacade, CSR_FACADE_PREFIX, HttpService, Logger, ProfileFacade } from '@contact/upgrade';

export enum PublisherActions {
  ON_SOUND = 'ON_SOUND',
  OFF_SOUND = 'OFF_SOUND',
  TOGGLE_SOUND = 'TOGGLE_SOUND',
  ON_VIDEO_CAMERA = 'ON_VIDEO_CAMERA',
  OFF_VIDEO_CAMERA = 'OFF_VIDEO_CAMERA',
  TOGGLE_VIDEO_CAMERA = 'TOGGLE_VIDEO_CAMERA',
  ON_VIDEO_SCREEN = 'ON_VIDEO_SCREEN',
  OFF_VIDEO_SCREEN = 'OFF_VIDEO_SCREEN',
  TOGGLE_VIDEO_SCREEN = 'TOGGLE_VIDEO_SCREEN',
}

export enum SubscriberProperties {
  hasVideo = 'hasVideo',
  hasAudio = 'hasAudio',
}

@Injectable({
  providedIn: 'root',
})
export class VisioService {
  session: OT.Session | undefined = undefined;
  publisherCamera: OT.Publisher | undefined = undefined;
  publisherScreen: OT.Publisher | undefined = undefined;

  isSessionConnected$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  subscriberStreamList$: BehaviorSubject<OT.Stream[] | undefined> = new BehaviorSubject([]);
  publisherCameraStream$: BehaviorSubject<OT.Stream | undefined> = new BehaviorSubject(undefined);
  publisherScreenStream$: BehaviorSubject<OT.Stream | undefined> = new BehaviorSubject(undefined);
  subscriberScreenStream$: Observable<OT.Stream | undefined> = this.subscriberStreamList$.pipe(
    map(streams => streams!.find(stream => stream.videoType === 'screen'))
  );
  screenSharingInProcess$: Observable<OT.Stream | undefined> = combineLatest([
    this.publisherScreenStream$,
    this.subscriberScreenStream$,
  ]).pipe(
    map(([publisher, subscriber]) => {
      if (!!publisher) {
        return publisher;
      } else if (!!subscriber) {
        return subscriber;
      } else {
        return;
      }
    })
  );
  subscriberChange$ = new Subject<any>();
  publisherActions$ = new Subject<PublisherActions>();
  login: string;
  visibleSkillsUuid: string[] = [];
  teamUuid: string;
  groupsUuid: string[] = [];

  constructor(
    private readonly logger: Logger,
    private readonly http: HttpService,
    private readonly OTService: OpentokService,
    private readonly agentConfigFacade: AgentConfigFacade,
    private readonly profileFacade: ProfileFacade
  ) {
    this.logger = this.logger.forSource('VisioService');
    this.profileFacade.login$.subscribe(login => (this.login = login));
    this.agentConfigFacade.visibleSkillIds$.subscribe(visibleSkillsUuid => (this.visibleSkillsUuid = visibleSkillsUuid));
    this.agentConfigFacade.team$.subscribe(team => (this.teamUuid = team ? team.id : ''));
    this.agentConfigFacade.groups$.subscribe(group => (this.groupsUuid = group.map(g => g.id)));
  }

  // GENERAL VISIO METHODS
  initializeSession(apiKey: string, token: string, sessionId: string): void {
    // initializeSession(): void {
    if (!!this.session) {
      return;
    }
    const s = this.OTService.initSession(apiKey, token, sessionId);
    // const s = this.OTService.oldInitSession();
    if (s) {
      s.then((session: OT.Session) => {
        this.session = session;
        this.session.on({
          sessionConnected: () => this.isSessionConnected$.next(true),
          streamCreated: event => {
            const streams = this.subscriberStreamList$.getValue() || [];
            this.subscriberStreamList$.next([...streams, event.stream]);
          },
          streamDestroyed: event => {
            const streams = this.subscriberStreamList$.getValue();
            this.subscriberStreamList$.next(streams!.filter(stream => stream.streamId !== event.stream.streamId));
          },
          streamPropertyChanged: event => {
            const subscriberProperty = SubscriberProperties[event.changedProperty as string];
            if (!!subscriberProperty) {
              this.subscriberChange$.next(event);
            }
          },
          'signal:actionRequest': event => {
            const { targetSessionUUID: targetId, action } = JSON.parse(event['data']);
            if (this.session && this.session.connection) {
              const publisherId = this.session.connection.connectionId;
              const publisherAction = PublisherActions[action as string];
              if (targetId === publisherId && !!publisherAction) {
                this.publisherActions$.next(publisherAction);
              }
            }
          },
        });
      }).then(() => this.OTService.connect());
    }
  }

  destroy() {
    if (!this.session) {
      return;
    }
    this.session.off({
      streamCreated: () => {},
      streamDestroyed: () => {},
      streamPropertyChanged: () => {},
      'signal:actionRequest': () => {},
    });
    this.isSessionConnected$.next(false);
    this.subscriberStreamList$.next([]);
    this.unpublishCamera();
    this.unpublishScreen();
  }

  sendAction(action: String, targetSessionUUID: String, targetName: String): any {
    if (this.session) {
      this.session.signal(
        {
          type: 'actionRequest',
          data: JSON.stringify({ action: action, targetSessionUUID: targetSessionUUID, targetName: targetName }),
        },
        () => {}
      );
    }
  }

  // END GENERAL VISIO METHODS

  // SUBSCRIBER METHODS
  subscribe(stream: OT.Stream, target: ElementRef, fullscreen?: boolean): OT.Subscriber | undefined {
    if (!this.session) {
      return undefined;
    }
    return this.session.subscribe(
      stream,
      target.nativeElement,
      {
        insertMode: 'append',
        width: '100%',
        height: fullscreen ? '100%' : '7em',
        style: {
          audioLevelDisplayMode: 'off',
          nameDisplayMode: 'off',
          buttonDisplayMode: 'off',
        },
      },
      () => {}
    );
  }

  unsubscribe(subscriber: OT.Subscriber) {
    if (!this.session) {
      return;
    }
    this.session.unsubscribe(subscriber);
  }

  // END SUBSCRIBER METHODS

  // PUBLISHER METHODS
  publishCamera(publisherDiv: ElementRef) {
    if (!this.session || this.publisherCamera) {
      this.unpublishCamera();
      return;
    }
    this.publisherCamera = OT.initPublisher(publisherDiv.nativeElement, {
      insertMode: 'append',
      width: '100%',
      height: '7em',
      style: {
        audioLevelDisplayMode: 'off',
        nameDisplayMode: 'off',
        buttonDisplayMode: 'off',
      },
    });
    this.session.publish(this.publisherCamera, () => {});
    this.publisherCamera.on('streamCreated', event => {
      this.publisherCameraStream$.next(event.stream);
    });
  }

  unpublishCamera() {
    if (!this.session || !this.publisherCamera) {
      return;
    }

    this.session.unpublish(this.publisherCamera);
    this.publisherCameraStream$.next(undefined);
    this.publisherCamera = undefined;
  }

  publishScreen(publisherDiv: ElementRef) {
    if (!this.session) {
      return;
    }
    if (!!this.publisherScreen && this.publisherScreen.stream) {
      this.unpublishScreen();
      return;
    }

    this.subscriberScreenStream$.pipe(take(1)).subscribe(stream => {
      if (!!stream) {
        this.sendAction(PublisherActions.TOGGLE_VIDEO_SCREEN, 'ALL', 'CSR');
      }
    });

    this.publisherScreen = OT.initPublisher(publisherDiv.nativeElement, {
      insertMode: 'append',
      videoSource: 'screen',
      width: '100%',
      height: '7em',
      style: {
        audioLevelDisplayMode: 'off',
        nameDisplayMode: 'off',
        buttonDisplayMode: 'off',
      },
    });
    this.publisherScreen.on({
      streamCreated: event => this.publisherScreenStream$.next(event.stream),
      streamDestroyed: () => {
        this.publisherScreenStream$.next(undefined);
        this.publisherScreen = undefined;
      },
    });
    this.session.publish(this.publisherScreen, () => {});
  }

  unpublishScreen() {
    if (!this.session || !this.publisherScreen) {
      return;
    }
    this.session.unpublish(this.publisherScreen);
    this.publisherScreenStream$.next(undefined);
    this.publisherScreen = undefined;
  }

  publisherOptionsStates(type: string, state: boolean) {
    if (!this.publisherCamera) {
      return;
    }
    switch (type) {
      case 'audio':
        this.publisherCamera.publishAudio(state);
        break;
      case 'video':
        this.publisherCamera.publishVideo(state);
        break;
      default:
        break;
    }
  }
  // END PUBLISHER METHODS

  // BASKET METHODS
  getRequests(
    filterBasket?: FieldList,
    periodFilter?: Criteria,
    status?: string,
    size?: number,
    sort?: Sort,
    from?: number
  ): Observable<IVisioRequestQueryResult> {
    let data: any = {};
    if (status === BasketVisioLabels.ASSIGNABLE) {
      data.request = toJson(buildCommonsRequests(this.teamUuid, this.groupsUuid, filterBasket, periodFilter, size, sort, from));
      data.uuidList = this.visibleSkillsUuid;
    } else {
      data.request = toJson(buildPersonalRequest(this.login, filterBasket, periodFilter, status, size, sort, from));
      data.uuidList = [];
    }

    return this.http
      .post(`${CSR_FACADE_PREFIX}/basket/appointment`, data, {
        observe: 'response',
      })
      .pipe(
        catchError((response: HttpResponse<any>) => {
          if (response.status === 401) {
            return throwError(() => 'commons:session.invalid_session');
          }
          return throwError(() => 'commons:session.unknow_error');
        }),
        map(response => {
          let { body } = response;
          return this.buildVisioRequestQueryResult(body);
        })
      );
  }
  // END BASKET METHODS

  openVisioAppointment(appointmentId: string): Observable<Object> {
    return this.http.withUrlParams([appointmentId]).get(CSR_FACADE_PREFIX + '/openVisioAppointment');
  }

  closeVisioAppointment(): Observable<Object> {
    return this.http.get(CSR_FACADE_PREFIX + '/closeVisioAppointment');
  }

  startVisioArchiving(appointmentId: string): Observable<Object> {
    return this.http.withUrlParams([appointmentId]).get(CSR_FACADE_PREFIX + '/startVisioArchiving', { responseType: 'text' });
  }

  stopVisioArchiving(appointmentId: string, openTokArchiveId: string): Observable<Object> {
    return this.http
      .withUrlParams([appointmentId, openTokArchiveId])
      .get(CSR_FACADE_PREFIX + '/stopVisioArchiving', { responseType: 'text' });
  }

  private buildVisioRequestQueryResult(body: any): IVisioRequestQueryResult {
    let hits = [];
    if (!!body && body.hits != null && body.hits.hits != null && body.hits.hits.length > 0) {
      hits = body.hits.hits.map(hit => hit._source);
    }
    return {
      total: !!body && body.hits != null && body.hits.total.value != null ? body.hits.total.value : 0,
      requests: hits,
    };
  }
}
